// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="TagSelect" set="mytaible-shadcn-studio" verified=tbd

import { X } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../../lib/utils';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface TagSelectProps {
  /** Full list of available options to choose from. */
  options: string[];
  /** Controlled array of selected values. */
  value: string[];
  /** Fires whenever the selection changes. */
  onChange: (values: string[]) => void;
  /** Placeholder shown inside the field when nothing is selected. */
  placeholder?: string;
  /** Shows a clear-all × button when at least one tag is selected. */
  clearable?: boolean;
  /** Additional CSS classes on the outer wrapper. */
  className?: string;
}

// ─── TagSelect ────────────────────────────────────────────────────────────────

/**
 * Inline tag-chip selector. Selected items appear as removable chips inside
 * the input border; an inline text filter narrows the option dropdown.
 *
 * Differs from `TagsInput` (free-text entry) and `MultiSelect` (button trigger
 * with checkbox list): this component renders an always-visible chip container
 * with an inline search that opens a floating option list.
 *
 * @example
 * const OPTS = ['React', 'Vue', 'Svelte'];
 * const [selected, setSelected] = useState<string[]>([]);
 * <TagSelect options={OPTS} value={selected} onChange={setSelected} />
 */
const TagSelect = React.forwardRef<HTMLDivElement, TagSelectProps>(
  ({ options, value, onChange, placeholder = 'Select…', clearable = false, className }, ref) => {
    const [search, setSearch] = React.useState('');
    const [open, setOpen] = React.useState(false);
    const containerRef = React.useRef<HTMLDivElement>(null);

    React.useImperativeHandle(ref, () => {
      if (!containerRef.current) {
        throw new Error('TagSelect ref is not mounted');
      }
      return containerRef.current;
    });

    const filtered = options.filter(
      (opt) => !value.includes(opt) && opt.toLowerCase().includes(search.toLowerCase()),
    );

    const add = (opt: string) => {
      onChange([...value, opt]);
      setSearch('');
    };

    const remove = (opt: string) => onChange(value.filter((v) => v !== opt));

    React.useEffect(() => {
      function onPointerDown(e: PointerEvent) {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
          setOpen(false);
          setSearch('');
        }
      }
      document.addEventListener('pointerdown', onPointerDown);
      return () => document.removeEventListener('pointerdown', onPointerDown);
    }, []);

    return (
      <div ref={containerRef} className={cn('relative', className)}>
        {/* ── Chip container + inline search ───────────────────────────────── */}
        <div
          className={cn(
            'flex min-h-[2.25rem] flex-wrap items-center gap-1.5',
            'rounded-[var(--field-radius,var(--radius))] border border-input bg-input-bg',
            'px-3 py-2 shadow-sm',
            'focus-within:outline-none focus-within:ring-2 focus-within:ring-ring',
            'focus-within:ring-offset-2 focus-within:ring-offset-background',
          )}
        >
          {value.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-sm font-medium text-primary"
            >
              {tag}
              <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => remove(tag)}
                className="cursor-pointer text-primary/60 hover:text-primary focus-visible:outline-none"
                aria-label={`Remove ${tag}`}
              >
                <X className="size-3" aria-hidden="true" />
              </button>
            </span>
          ))}

          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder={value.length === 0 ? placeholder : ''}
            className="min-w-[4rem] flex-1 bg-transparent text-base text-foreground placeholder:text-muted-foreground focus:outline-none sm:text-sm"
          />

          {clearable && value.length > 0 && (
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => onChange([])}
              className="ml-auto cursor-pointer text-muted-foreground hover:text-foreground focus-visible:outline-none"
              aria-label="Clear all"
            >
              <X className="size-4" aria-hidden="true" />
            </button>
          )}
        </div>

        {/* ── Option dropdown ───────────────────────────────────────────────── */}
        {open && filtered.length > 0 && (
          <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-[var(--field-radius,var(--radius))] border border-input bg-popover shadow-md">
            {filtered.map((opt) => (
              <button
                key={opt}
                type="button"
                onPointerDown={(e) => e.preventDefault()}
                onClick={() => add(opt)}
                className="w-full cursor-pointer px-4 py-2.5 text-left text-sm text-foreground hover:bg-accent"
              >
                {opt}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  },
);
TagSelect.displayName = 'TagSelect';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { TagSelect };
